import type {
  CreateRequiredDocPayload,
  RequiredDoc,
  RequiredDocStatus,
  UpdateRequiredDocPayload,
} from "@/features/required-docs/types";
import { apiFetch } from "@/lib/api";

/** Paginated list envelope: `{ success, message, data, meta }`. */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiRequiredDoc {
  id: string;
  name: string;
  notes: string;
  status: RequiredDocStatus;
  due_date: string | null;
  requested_by: string | null;
  requested_by_name: string;
  received_at: string | null;
  created_at: string;
  updated_at: string;
}

function toRequiredDoc(a: ApiRequiredDoc): RequiredDoc {
  return {
    id: a.id,
    name: a.name,
    notes: a.notes,
    status: a.status,
    dueDate: a.due_date,
    requestedBy: a.requested_by,
    requestedByName: a.requested_by_name,
    receivedAt: a.received_at,
    createdAt: a.created_at,
    updatedAt: a.updated_at,
  };
}

/** GET /clients/:id/required-documents/ — the client's required-document checklist. */
export async function listRequiredDocs(clientId: string): Promise<RequiredDoc[]> {
  const body = await apiFetch<Envelope<ApiRequiredDoc[]>>(
    `/clients/${clientId}/required-documents/?page_size=200`,
  );
  return body.data.map(toRequiredDoc);
}

/** POST /clients/:id/required-documents/ — add a checklist item (status defaults PENDING). */
export async function createRequiredDoc(
  clientId: string,
  payload: CreateRequiredDocPayload,
): Promise<RequiredDoc> {
  const body = await apiFetch<Envelope<ApiRequiredDoc>>(
    `/clients/${clientId}/required-documents/`,
    { method: "POST", json: payload },
  );
  return toRequiredDoc(body.data);
}

/**
 * PATCH /clients/:id/required-documents/:docId/ — update status and/or fields.
 * `received_at` is auto-stamped/cleared server-side from `status`; never send it.
 */
export async function updateRequiredDoc(
  clientId: string,
  docId: string,
  payload: UpdateRequiredDocPayload,
): Promise<RequiredDoc> {
  const body = await apiFetch<Envelope<ApiRequiredDoc>>(
    `/clients/${clientId}/required-documents/${docId}/`,
    { method: "PATCH", json: payload },
  );
  return toRequiredDoc(body.data);
}

/** DELETE /clients/:id/required-documents/:docId/ */
export function deleteRequiredDoc(clientId: string, docId: string): Promise<void> {
  return apiFetch<void>(`/clients/${clientId}/required-documents/${docId}/`, {
    method: "DELETE",
  });
}
